import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { B2BService } from 'src/app/b2-b.service';
import { SharedService } from 'src/app/shared.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IdObfuscator } from 'src/app/utils/id-obfuscator.util';

// Interface matching backend ProductClaimProof class
export interface ProductClaimProof {
  ID: number;
  FKClaimProductID: number;
  UploadedDocument: any; // byte[] - not used in frontend
  Base64UploadedDocument: string; // ✅ MUST match C# property name (singular)
  FileName?: string; // For UI display
}

// Interface matching backend ClaimProduct class
export interface ClaimProduct {
  FKClaimID: number;
  DBID: number;
  DocumentID: string;
  IndividualProductDocumentID: string;
  Name: string;
  Code: string;
  Description: string;
  DescriptionRows: any[]; // Array of description rows
  ClaimedQuantity: number;
  DocQuantity: number;
  ClaimType: any;
  UserClaimDescription: string; // User's description of the problem
  Proofs: ProductClaimProof[];
  Status: any;
  Solution: any;
}

export interface ProductValidationError {
  product: ClaimProduct;
  missingClaimType: boolean;
  missingDescription: boolean;
  missingProof: boolean;
}

@Component({
  selector: 'app-clientclaim',
  templateUrl: './clientclaim.component.html',
  styleUrls: ['./clientclaim.component.css'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: '0', overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: '1' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: '1', overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0', opacity: '0' }))
      ])
    ])
  ]
})
export class ClientclaimComponent implements OnInit {

  // Invoice search input reference
  @ViewChild('invoiceSearchInput') invoiceSearchInput!: ElementRef<HTMLInputElement>;
  invoiceSearchHighlighted: boolean = false;

  // Invoice search
  invoiceSearchTerm: string = '';
  invoiceSearchTermUpdate = new Subject<string>();
  searchedInvoices: any[] = [];
  showInvoiceResults: boolean = false;
  loadingInvoices: boolean = false;
  selectedInvoice: any = null;

  // Claim products (from selected invoice)
  claimProducts: ClaimProduct[] = [];
  productClaimTypes: any[] = [];
  loadingProducts: boolean = false;
  openClaimTypeDropdownIndex: number = -1; // Track which product's dropdown is open
  productsExpanded: boolean = true; // Track if products section is expanded

  // Claim form fields
  claimTitle: string = '';

  // File upload (for product proofs)
  acceptedFileTypes: string = '.jpg,.jpeg,.mp4';

  // Submission
  submitting: boolean = false;
  submitSuccess: boolean = false;
  submitError: string = '';

  // Product validation errors
  productValidationErrors: ProductValidationError[] = [];
  showProductValidationErrors: boolean = false;

  // Tab switching
  showNewClaimTab: boolean = false; // false = My Claims, true = New Claim

  // My Claims list
  claims: any[] = [];
  Filter: any = {};
  loadingClaims: boolean = false;

  // View mode (for viewing existing claims) - SEPARATE from New Claim data
  viewMode: boolean = false; // false = create mode, true = view mode
  selectedClaim: any = null; // The claim being viewed
  viewClaimProducts: ClaimProduct[] = []; // Products for VIEW MODE ONLY
  viewClaimTitle: string = ''; // Title for VIEW MODE ONLY
  viewSelectedInvoice: any = null; // Invoice for VIEW MODE ONLY
  claimComments: any[] = []; // Comments for the selected claim
  newComment: string = ''; // New comment text
  newCommentFile: File | null = null; // Optional file for new comment
  newCommentFileName: string = ''; // File name for display
  submittingComment: boolean = false;
  loadingClaimDetails: boolean = false;

  // Reply functionality
  replyingToCommentID: number | null = null; // ID of comment being replied to
  newReply: string = ''; // New reply text
  newReplyFile: File | null = null; // Optional file for new reply
  newReplyFileName: string = ''; // File name for display
  submittingReply: boolean = false;

  // Collapsible sections for view mode
  viewProductsSectionExpanded: boolean = true; // View mode products section expanded by default
  viewCommentsSectionExpanded: boolean = true; // View mode comments section expanded by default

  // Partner Agents for Claim (View Mode)
  claimPartnerAgents: any[] = []; // All partner agents for the claim's partner
  loadingPartnerAgents: boolean = false; // Loading state for partner agents
  togglingAgentId: number | null = null; // Track which agent is being toggled
  viewAgentsSectionExpanded: boolean = true; // Agents section expanded by default

  // Selected Partner Agent for comment/reply attribution
  selectedCommentAgent: any = null;
  selectedReplyAgent: any = null;
  commentAgentDropdownOpen: boolean = false;
  replyAgentDropdownOpen: boolean = false;

  // Partner Agents for New Claim (Create Mode)
  newClaimPartnerAgents: any[] = []; // All partner agents for the new claim's partner
  newClaimLoadingPartnerAgents: boolean = false; // Loading state for new claim partner agents
  newClaimAgentsSectionExpanded: boolean = true; // Agents section expanded by default

  // Standalone properties (no parent dependency)
  // Robot user ID for backend interactions - loaded via GetUser for docid flows
  RobotUserId: string = '63e5f21201d04d74b17ea834c5b587df5d0ffb5017ed46cca634df7c7e43b249';
  User: any = {
    Id: '63e5f21201d04d74b17ea834c5b587df5d0ffb5017ed46cca634df7c7e43b249',
  };
  Partner: any = null;
  Company: any = null;

  // URL query parameters
  urlPartnerId: string | null = null;
  urlClaimId: string | null = null;
  urlDocumentId: string | null = null;
  urlDirection: number | null = null; // -1 = reception/import, 1 = invoice/export
  urlCompanyId: number | null = null;

  // Loading states for URL parameter handling
  loadingPartner: boolean = false;
  initializationComplete: boolean = false;

  // Page title (displayed above tab switcher)
  pageTitle: string = '';
  showPageTitle: boolean = false;

  // Error state (Apple iPhone-style error message)
  showError: boolean = false;
  errorMessage: string = '';
  errorType: 'missing_params' | 'partner_not_found' | 'claim_not_found' | null = null;

  constructor(
    private route: ActivatedRoute,
    private B2BService: B2BService,
    private sharedService: SharedService,
    private _snackBar: MatSnackBar
  ) {
    // Debounce invoice search
    this.invoiceSearchTermUpdate.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      if (value?.length >= 2) {
        this.searchInvoices(value);
      } else {
        this.searchedInvoices = [];
        this.showInvoiceResults = false;
      }
    });
  }

  // Language helper methods (standalone - no parent dependency)
  getLanguage(): string {
    try {
      return localStorage?.getItem('language') ?? 'en';
    } catch (e) {
      console.warn('Could not access localStorage:', e);
      return 'en';
    }
  }

  // Snackbar helper method (standalone - no parent dependency)
  openSnackBar(message: string, action: string, designClass: string): void {
    this._snackBar.open(message, action, {
      duration: 5000,
      panelClass: [designClass]
    });
  }

  ngOnInit(): void {
    this.processUrlParameters();
  }

  // Process URL query parameters
  processUrlParameters(): void {
    this.route.queryParams.subscribe(params => {
      const rawPid = params['pid'] || null;
      const rawId = params['id'] || null;
      const rawDocId = params['docid'] || null;
      const rawDir = params['dir'] || null;
      const rawCid = params['cid'] || null;

      // Decode the partner ID if it's obfuscated
      if (rawPid) {
        this.urlPartnerId = IdObfuscator.decode(rawPid);
      } else {
        this.urlPartnerId = null;
      }

      // Decode the claim ID if it's obfuscated
      if (rawId) {
        this.urlClaimId = IdObfuscator.decode(rawId);
      } else {
        this.urlClaimId = null;
      }

      // Decode the document ID if it's obfuscated
      if (rawDocId) {
        this.urlDocumentId = IdObfuscator.decode(rawDocId);
      } else {
        this.urlDocumentId = null;
      }

      // Parse direction
      if (rawDir) {
        this.urlDirection = parseInt(rawDir, 10);
      } else {
        this.urlDirection = null;
      }

      // Decode the company ID
      if (rawCid) {
        var decodedCid = IdObfuscator.decode(rawCid);
        this.urlCompanyId = decodedCid ? parseInt(decodedCid, 10) : null;
      } else {
        this.urlCompanyId = null;
      }

      // Reset error state
      this.showError = false;
      this.errorMessage = '';
      this.errorType = null;

      if (this.urlClaimId) {
        // Load existing claim by ID (view mode)
        this.viewMode = true;
        this.showNewClaimTab = false;
        const claimId = parseInt(this.urlClaimId, 10);
        if (!isNaN(claimId) && claimId > 0) {
          this.loadClaimDetails(claimId);
        } else {
          // Invalid claim ID
          this.showError = true;
          this.errorType = 'claim_not_found';
          this.showPageTitle = false;
          this.pageTitle = '';
        }
      } else if (this.urlDocumentId && this.urlDirection) {
        // Fast new claim: load document by ID, extract partner from it (skip search step)
        this.loadDocumentAndInitializeClaim(this.urlDocumentId, this.urlDirection);
      } else if (this.urlPartnerId) {
        // Load partner by ID - show both tabs, filter claims by partner
        this.loadPartnerById(this.urlPartnerId);
      } else {
        // NEITHER pid NOR id provided - show error
        this.showError = true;
        this.errorType = 'missing_params';
        this.showPageTitle = false;
        this.pageTitle = '';
      }

      this.initializationComplete = true;
    });
  }

  // Load partner by ID from URL parameter
  loadPartnerById(partnerId: string): void {
    this.loadingPartner = true;

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      PartnerID: partnerId
    };

    this.sharedService.GetPartner(request).subscribe(
      (response: any) => {
        this.loadingPartner = false;
        // Check Status, Partner object exists, AND Partner.ID is valid (not null/undefined/0)
        if (response.Status === 1 && response.Partner && response.Partner.ID) {
          this.Partner = response.Partner;
          // Set page title with partner name
          this.pageTitle = this.Partner.Name || '';
          this.showPageTitle = true;
          // Show New Claim tab by default (user can switch to My Claims)
          this.showNewClaimTab = true;
          this.viewMode = false;
          this.InitializeFilter();
          this.GetMyClaimsFiltered();
        } else {
          // Partner NOT found or Partner.ID is null - show Apple-style error
          this.showError = true;
          this.errorType = 'partner_not_found';
          this.showPageTitle = false;
          this.pageTitle = '';
        }
      },
      (error) => {
        this.loadingPartner = false;
        console.error('Error loading partner:', error);
        // Partner NOT found - show Apple-style error
        this.showError = true;
        this.errorType = 'partner_not_found';
        this.showPageTitle = false;
        this.pageTitle = '';
      }
    );
  }

  // Fast new claim: load robot user, set company, then load document by ID
  loadDocumentAndInitializeClaim(documentId: string, direction: number): void {
    this.loadingPartner = true;

    // Step 1: Get robot user with full details
    var userRequest = {
      User: { Id: this.RobotUserId },
      Language: this.getLanguage(),
      id: this.RobotUserId,
      WithDetails: true
    };

    this.sharedService.GetUser(userRequest).subscribe(
      (userResponse: any) => {
        if (userResponse.Status === 1 && userResponse.User) {
          this.User = userResponse.User;

          // Set IsSelected on the company matching cid
          if (this.urlCompanyId && this.User.Rights?.AssignedCompanies) {
            this.User.Rights.AssignedCompanies.forEach((c: any) => {
              c.IsSelected = (c.ID === this.urlCompanyId);
            });
          }

          // Step 2: Load document
          this.loadDocumentByDirection(documentId, direction);
        } else {
          this.loadingPartner = false;
          this.showError = true;
          this.errorType = 'missing_params';
          this.showPageTitle = false;
          this.pageTitle = '';
        }
      },
      () => {
        this.loadingPartner = false;
        this.showError = true;
        this.errorType = 'missing_params';
        this.showPageTitle = false;
        this.pageTitle = '';
      }
    );
  }

  // Step 2: Load document after user is ready
  private loadDocumentByDirection(documentId: string, direction: number): void {
    if (direction === -1) {
      // Reception/import
      var receptionRequest = {
        User: this.User,
        Language: this.getLanguage(),
        ID: documentId,
        WithInfo: true
      };

      this.sharedService.GetReception(receptionRequest).subscribe(
        (response: any) => {
          this.loadingPartner = false;
          if (response.Status === 1 && response.Reception) {
            this.Partner = response.Reception.Supplier;
            this.pageTitle = this.Partner?.Name || '';
            this.showPageTitle = true;
            this.showNewClaimTab = true;
            this.viewMode = false;
            this.InitializeFilter();

            this.selectedInvoice = response.Reception;
            this.loadReceptionProducts(documentId);
            this.loadNewClaimPartnerAgents();
          } else {
            this.showError = true;
            this.errorType = 'missing_params';
            this.showPageTitle = false;
            this.pageTitle = '';
          }
        },
        () => {
          this.loadingPartner = false;
          this.showError = true;
          this.errorType = 'missing_params';
          this.showPageTitle = false;
          this.pageTitle = '';
        }
      );
    } else {
      // Invoice/export
      var invoiceRequest = {
        User: this.User,
        Language: this.getLanguage(),
        ID: documentId,
        WithInfo: true
      };

      this.sharedService.GetInvoice(invoiceRequest).subscribe(
        (response: any) => {
          this.loadingPartner = false;
          if (response.Status === 1 && response.Invoice) {
            this.Partner = response.Invoice.Client;
            this.pageTitle = this.Partner?.Name || '';
            this.showPageTitle = true;
            this.showNewClaimTab = true;
            this.viewMode = false;
            this.InitializeFilter();

            this.selectedInvoice = response.Invoice;
            this.loadInvoiceProducts(documentId);
            this.loadNewClaimPartnerAgents();
          } else {
            this.showError = true;
            this.errorType = 'missing_params';
            this.showPageTitle = false;
            this.pageTitle = '';
          }
        },
        () => {
          this.loadingPartner = false;
          this.showError = true;
          this.errorType = 'missing_params';
          this.showPageTitle = false;
          this.pageTitle = '';
        }
      );
    }
  }

  // Load reception products for claim (direction = -1)
  loadReceptionProducts(receptionID: string): void {
    this.loadingProducts = true;
    this.claimProducts = [];
    this.productClaimTypes = [];

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      ReceptionID: receptionID
    };

    this.B2BService.GetReceptionProductsForClaim(request).subscribe(
      (response: any) => {
        this.loadingProducts = false;
        if (response.Status === 1) {
          this.claimProducts = response.ClaimProducts || [];
          this.productClaimTypes = response.ProductClaimTypes || [];

          this.claimProducts.forEach(product => {
            if (!product.Proofs) {
              product.Proofs = [];
            }
          });
        } else {
          this.openSnackBar(response.Message || 'Failed to load products', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingProducts = false;
        console.error('Error loading reception products:', error);
        this.openSnackBar('Failed to load reception products', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Initialize filter for My Claims
  InitializeFilter(): void {
    this.Filter = {};
    this.Filter.Pager = {};
    this.Filter.Pager.PageIndex = 1;
    this.Filter.Pager.PageSize = 50;
    this.Filter.Pager.TotalCount = 0;
    this.Filter.Partner = this.Partner;
    this.Filter.ClaimGeneralType = null; // null = all types, -1 = invoice claims, 1 = reception claims
  }

  // Get filtered claims list
  GetMyClaimsFiltered(page = 1): void {
    this.loadingClaims = true;
    this.Filter.Pager.PageIndex = page;
    // ALWAYS ensure Partner from URL is included in the filter
    this.Filter.Partner = this.Partner;

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      Filter: this.Filter
    };

    this.B2BService.GetMyClaimsFiltered(request).subscribe(
      (response: any) => {
        this.loadingClaims = false;
        if (response.Status === 1) {
          this.claims = response.Claims || [];
          this.Filter.Pager = response.Pager;
          // Preserve Partner after response overwrites Pager
          this.Filter.Partner = this.Partner;
        } else {
          this.openSnackBar(response.Message, 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingClaims = false;
        this.openSnackBar('Failed to load claims', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Tab switching methods
  showMyClaimsTab(): void {
    this.showNewClaimTab = false;
    this.viewMode = false;
    this.selectedClaim = null;
    // Ensure filter is initialized before fetching claims
    if (!this.Filter.Pager) {
      this.InitializeFilter();
    }
    this.GetMyClaimsFiltered();
  }

  showNewClaimTabClick(): void {
    this.showNewClaimTab = true;
    this.viewMode = false;
    this.selectedClaim = null;
  }

  // Open claim details in view mode
  openClaimDetails(claim: any): void {
    this.viewMode = true;
    this.showNewClaimTab = false;
    this.selectedClaim = claim;
    this.loadClaimDetails(claim.ID);
  }

  // Load claim details by ID
  loadClaimDetails(claimID: number): void {
    this.loadingClaimDetails = true;

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      ClaimID: claimID
    };

    this.B2BService.GetClaimByID(request).subscribe(
      (response: any) => {
        this.loadingClaimDetails = false;
        if (response.Status === 1 && response.Claim) {
          this.selectedClaim = response.Claim;
          // Use SEPARATE view-mode objects (don't overwrite New Claim data)
          this.viewClaimProducts = response.Claim.ClaimedProducts || [];
          this.claimComments = response.Claim.Comments || [];
          this.viewClaimTitle = response.Claim.Title;
          this.viewSelectedInvoice = { Reference: response.Claim.DebatorPO };

          // Set page title from claim.Partner.Name (when accessed via ?id= URL param)
          if (response.Claim.Partner?.Name) {
            this.Partner = response.Claim.Partner;
            this.pageTitle = response.Claim.Partner.Name;
            this.showPageTitle = true;
            // Initialize filter with Partner from claim
            this.InitializeFilter();
          }

          // Load partner agents for this claim
          this.loadClaimPartnerAgents();

          // Clear any error state
          this.showError = false;
          this.errorType = null;
        } else {
          // Claim NOT found - show Apple-style error (only if accessed via URL param)
          if (this.urlClaimId) {
            this.showError = true;
            this.errorType = 'claim_not_found';
            this.showPageTitle = false;
            this.pageTitle = '';
            this.viewMode = false;
          } else {
            this.openSnackBar(response.Message || 'Failed to load claim details', 'OK', 'alert-info-snackbar');
          }
        }
      },
      (error) => {
        this.loadingClaimDetails = false;
        console.error('Error loading claim details:', error);
        // Claim NOT found - show Apple-style error (only if accessed via URL param)
        if (this.urlClaimId) {
          this.showError = true;
          this.errorType = 'claim_not_found';
          this.showPageTitle = false;
          this.pageTitle = '';
          this.viewMode = false;
        } else {
          this.openSnackBar('Failed to load claim details', 'OK', 'alert-info-snackbar');
        }
      }
    );
  }

  // Back to claims list from view mode
  backToClaimsList(): void {
    this.viewMode = false;
    this.selectedClaim = null;
    // Clear view-mode specific data
    this.viewClaimProducts = [];
    this.viewClaimTitle = '';
    this.viewSelectedInvoice = null;
    this.claimComments = [];
    this.newComment = '';
    this.newCommentFile = null;
    this.newCommentFileName = '';
    // Clear partner agents data
    this.claimPartnerAgents = [];
    this.loadingPartnerAgents = false;
    this.togglingAgentId = null;
    // Clear selected agents for comment/reply
    this.selectedCommentAgent = null;
    this.selectedReplyAgent = null;
    this.commentAgentDropdownOpen = false;
    this.replyAgentDropdownOpen = false;
    this.GetMyClaimsFiltered();
  }

  // Handle comment file selection
  onCommentFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const validVideoTypes = ['video/mp4'];
      const validTypes = [...validImageTypes, ...validVideoTypes];

      if (!validTypes.includes(file.type)) {
        this.openSnackBar('Invalid file type. Only JPEG, PNG and MP4 files are allowed.', 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      // Validate file size - 10MB for images, 50MB for videos
      const isVideo = validVideoTypes.includes(file.type);
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      const maxSizeLabel = isVideo ? '50MB' : '10MB';

      if (file.size > maxSize) {
        this.openSnackBar(`File size must be less than ${maxSizeLabel}.`, 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      this.newCommentFile = file;
      this.newCommentFileName = file.name;
    }
  }

  // Remove selected comment file
  removeCommentFile(): void {
    this.newCommentFile = null;
    this.newCommentFileName = '';
  }

  // Add comment to claim
  addComment(): void {
    if (!this.newComment.trim()) {
      this.openSnackBar('Please enter a comment', 'OK', 'alert-info-snackbar');
      return;
    }

    // Validate partner agent selection
    if (!this.selectedCommentAgent) {
      this.openSnackBar('Please select a Partner Agent', 'OK', 'alert-info-snackbar');
      return;
    }

    this.submittingComment = true;

    // Convert file to Base64 if present
    if (this.newCommentFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64File = (reader.result as string).split(',')[1];
        this.submitComment(base64File);
      };
      reader.readAsDataURL(this.newCommentFile);
    } else {
      this.submitComment('');
    }
  }

  // Download proof attachment
  downloadProof(proof: any, index: number): void {
    if (!proof.Base64UploadedDocument) {
      this.openSnackBar('No file available for download', 'OK', 'alert-info-snackbar');
      return;
    }

    try {
      // Determine file extension from Base64 header or default to jpg
      const base64Data = proof.Base64UploadedDocument;
      let mimeType = 'image/jpeg';
      let extension = 'jpg';

      // Try to detect file type from Base64 data
      if (base64Data.startsWith('/9j/')) {
        mimeType = 'image/jpeg';
        extension = 'jpg';
      } else if (base64Data.startsWith('iVBORw0KGgo')) {
        mimeType = 'image/png';
        extension = 'png';
      } else if (base64Data.startsWith('AAAA')) {
        mimeType = 'video/mp4';
        extension = 'mp4';
      }

      // Convert Base64 to Blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proof_${index + 1}.${extension}`;
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading proof:', error);
      this.openSnackBar('Failed to download file', 'OK', 'alert-info-snackbar');
    }
  }

  // Download comment attachment
  downloadCommentAttachment(comment: any): void {
    if (!comment.Base64UploadedDocument) {
      this.openSnackBar('No file available for download', 'OK', 'alert-info-snackbar');
      return;
    }

    try {
      // Determine file extension from Base64 header or default to jpg
      const base64Data = comment.Base64UploadedDocument;
      let mimeType = 'image/jpeg';
      let extension = 'jpg';

      // Try to detect file type from Base64 data
      if (base64Data.startsWith('/9j/')) {
        mimeType = 'image/jpeg';
        extension = 'jpg';
      } else if (base64Data.startsWith('iVBORw0KGgo')) {
        mimeType = 'image/png';
        extension = 'png';
      } else if (base64Data.startsWith('AAAA')) {
        mimeType = 'video/mp4';
        extension = 'mp4';
      }

      // Convert Base64 to Blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comment_attachment_${comment.ID}.${extension}`;
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      this.openSnackBar('Failed to download file', 'OK', 'alert-info-snackbar');
    }
  }

  // Submit comment to backend
  submitComment(base64File: string): void {
    const request = {
      User: this.User,
      Language: this.getLanguage(),
      ClaimComment: {
        FKClaimID: this.selectedClaim.ID,
        Comment: this.newComment,
        Base64UploadedDocument: base64File,
        UserEmail: this.selectedCommentAgent?.Email || ''
      }
    };

    this.B2BService.AddClaimComment(request).subscribe(
      (response: any) => {
        this.submittingComment = false;
        if (response.Status === 1) {
          // Add the new comment to the list
          this.claimComments.push(response.ClaimComment);
          // Reset form
          this.newComment = '';
          this.newCommentFile = null;
          this.newCommentFileName = '';
          this.openSnackBar('Comment added successfully', 'OK', 'light-info-snackbar');
        } else {
          this.openSnackBar(response.Message || 'Failed to add comment', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.submittingComment = false;
        this.openSnackBar('Failed to add comment', 'OK', 'alert-info-snackbar');
        console.error('Error adding comment:', error);
      }
    );
  }

  // Start replying to a comment
  startReply(comment: any): void {
    this.replyingToCommentID = comment.ID;
    this.newReply = '';
    this.newReplyFile = null;
    this.newReplyFileName = '';
  }

  // Cancel reply
  cancelReply(): void {
    this.replyingToCommentID = null;
    this.newReply = '';
    this.newReplyFile = null;
    this.newReplyFileName = '';
    this.selectedReplyAgent = null;
    this.replyAgentDropdownOpen = false;
  }

  // Handle reply file selection
  onReplyFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const validVideoTypes = ['video/mp4'];
      const validTypes = [...validImageTypes, ...validVideoTypes];

      if (!validTypes.includes(file.type)) {
        this.openSnackBar('Invalid file type. Only JPEG, PNG and MP4 files are allowed.', 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      // Validate file size - 10MB for images, 50MB for videos
      const isVideo = validVideoTypes.includes(file.type);
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      const maxSizeLabel = isVideo ? '50MB' : '10MB';

      if (file.size > maxSize) {
        this.openSnackBar(`File size must be less than ${maxSizeLabel}.`, 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      this.newReplyFile = file;
      this.newReplyFileName = file.name;
    }
  }

  // Remove selected reply file
  removeReplyFile(): void {
    this.newReplyFile = null;
    this.newReplyFileName = '';
  }

  // Add reply (convert file to Base64 and submit)
  addReply(): void {
    if (!this.newReply || !this.replyingToCommentID) {
      return;
    }

    // Validate partner agent selection
    if (!this.selectedReplyAgent) {
      this.openSnackBar('Please select a Partner Agent', 'OK', 'alert-info-snackbar');
      return;
    }

    this.submittingReply = true;

    // Convert file to Base64 if present
    if (this.newReplyFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64File = (reader.result as string).split(',')[1];
        this.submitReply(base64File);
      };
      reader.readAsDataURL(this.newReplyFile);
    } else {
      this.submitReply('');
    }
  }

  // Submit reply to backend
  submitReply(base64File: string): void {
    const request = {
      User: this.User,
      Language: this.getLanguage(),
      ClaimCommentReply: {
        FKClaimCommentID: this.replyingToCommentID,
        Reply: this.newReply,
        Base64UploadedDocument: base64File,
        UserEmail: this.selectedReplyAgent?.Email || ''
      }
    };

    this.B2BService.AddClaimCommentReply(request).subscribe(
      (response: any) => {
        this.submittingReply = false;
        if (response.Status === 1) {
          // Find the parent comment and add the reply to it
          const parentComment = this.claimComments.find(c => c.ID === this.replyingToCommentID);
          if (parentComment) {
            if (!parentComment.Replies) {
              parentComment.Replies = [];
            }
            parentComment.Replies.push(response.ClaimCommentReply);
          }
          // Reset reply form
          this.cancelReply();
          this.openSnackBar('Reply added successfully', 'OK', 'light-info-snackbar');
        } else {
          this.openSnackBar(response.Message || 'Failed to add reply', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.submittingReply = false;
        this.openSnackBar('Failed to add reply', 'OK', 'alert-info-snackbar');
        console.error('Error adding reply:', error);
      }
    );
  }

  // Download reply attachment
  downloadReplyAttachment(reply: any): void {
    if (!reply.Base64UploadedDocument) {
      this.openSnackBar('No file available for download', 'OK', 'alert-info-snackbar');
      return;
    }

    try {
      // Detect file type from Base64 header
      let mimeType = 'application/octet-stream';
      let extension = 'bin';

      if (reply.Base64UploadedDocument.startsWith('/9j/')) {
        mimeType = 'image/jpeg';
        extension = 'jpg';
      } else if (reply.Base64UploadedDocument.startsWith('iVBORw0KGgo')) {
        mimeType = 'image/png';
        extension = 'png';
      } else if (reply.Base64UploadedDocument.startsWith('AAAA')) {
        mimeType = 'video/mp4';
        extension = 'mp4';
      }

      // Convert Base64 to Blob
      const byteCharacters = atob(reply.Base64UploadedDocument);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reply_${reply.ID}.${extension}`;
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading reply attachment:', error);
      this.openSnackBar('Failed to download file', 'OK', 'alert-info-snackbar');
    }
  }

  // Toggle collapsible sections for view mode
  toggleViewProductsSection(): void {
    this.viewProductsSectionExpanded = !this.viewProductsSectionExpanded;
  }

  toggleViewCommentsSection(): void {
    this.viewCommentsSectionExpanded = !this.viewCommentsSectionExpanded;
  }

  // Toggle Agents Section expand/collapse
  toggleViewAgentsSection(): void {
    this.viewAgentsSectionExpanded = !this.viewAgentsSectionExpanded;
  }

  // Load all partner agents for the claim's partner, then mark selected ones
  loadClaimPartnerAgents(): void {
    if (!this.selectedClaim?.Partner?.ID) {
      return;
    }

    this.loadingPartnerAgents = true;
    this.claimPartnerAgents = [];

    const request = {
      Language: this.getLanguage(),
      PartnerID: this.selectedClaim.Partner.ID
    };

    this.sharedService.GetPartnerAgents(request).subscribe(
      (response: any) => {
        if (response.Status === 1) {
          this.claimPartnerAgents = response.PartnerAgents || [];
          // Mark all as unselected initially
          this.claimPartnerAgents.forEach(agent => agent.IsSelected = false);
          // Now load claim-specific agents to mark them as selected
          this.loadClaimSpecificPartnerAgents();
        } else {
          this.loadingPartnerAgents = false;
        }
      },
      () => {
        this.loadingPartnerAgents = false;
      }
    );
  }

  // Load claim-specific partner agents and mark them as selected
  loadClaimSpecificPartnerAgents(): void {
    if (!this.selectedClaim?.ID) {
      this.loadingPartnerAgents = false;
      return;
    }

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      ClaimID: this.selectedClaim.ID
    };

    this.B2BService.GetClaimPartnerAgents(request).subscribe(
      (response: any) => {
        this.loadingPartnerAgents = false;
        if (response.Status === 1 && response.PartnerAgents) {
          // Mark agents that are associated with this claim as selected
          const selectedAgentIDs = response.PartnerAgents.map((a: any) => a.ID);
          this.claimPartnerAgents.forEach(agent => {
            agent.IsSelected = selectedAgentIDs.includes(agent.ID);
            // Copy Department info if available from response
            const matchedAgent = response.PartnerAgents.find((a: any) => a.ID === agent.ID);
            if (matchedAgent?.Department) {
              agent.Department = matchedAgent.Department;
            }
          });
        }
      },
      () => {
        this.loadingPartnerAgents = false;
      }
    );
  }

  // Toggle partner agent association with claim
  toggleClaimPartnerAgent(agent: any): void {
    if (this.togglingAgentId !== null || !this.selectedClaim?.ID) {
      return; // Prevent multiple simultaneous toggles
    }

    this.togglingAgentId = agent.ID;
    const wasSelected = agent.IsSelected;

    // Optimistic UI update
    agent.IsSelected = !wasSelected;

    if (wasSelected) {
      // Remove agent from claim
      const request = {
        User: this.User,
        Language: this.getLanguage(),
        PartnerAgentID: agent.ID,
        ClaimID: this.selectedClaim.ID
      };

      this.B2BService.DeleteClaimPartnerAgent(request).subscribe(
        (response: any) => {
          this.togglingAgentId = null;
          if (response.Status !== 1) {
            // Revert on failure
            agent.IsSelected = wasSelected;
            this.openSnackBar(response.Message || 'Failed to remove agent', 'OK', 'alert-info-snackbar');
          }
        },
        () => {
          this.togglingAgentId = null;
          agent.IsSelected = wasSelected;
          this.openSnackBar('Failed to remove agent', 'OK', 'alert-info-snackbar');
        }
      );
    } else {
      // Add agent to claim
      const request = {
        User: this.User,
        Language: this.getLanguage(),
        PartnerAgentID: agent.ID,
        ClaimID: this.selectedClaim.ID
      };

      this.B2BService.AddClaimPartnerAgent(request).subscribe(
        (response: any) => {
          this.togglingAgentId = null;
          if (response.Status !== 1) {
            // Revert on failure
            agent.IsSelected = wasSelected;
            this.openSnackBar(response.Message || 'Failed to add agent', 'OK', 'alert-info-snackbar');
          }
        },
        () => {
          this.togglingAgentId = null;
          agent.IsSelected = wasSelected;
          this.openSnackBar('Failed to add agent', 'OK', 'alert-info-snackbar');
        }
      );
    }
  }

  // Comment Agent Dropdown methods
  toggleCommentAgentDropdown(): void {
    if (this.submittingComment || this.loadingPartnerAgents) {
      return;
    }
    this.commentAgentDropdownOpen = !this.commentAgentDropdownOpen;
  }

  closeCommentAgentDropdown(): void {
    this.commentAgentDropdownOpen = false;
  }

  selectCommentAgent(agent: any): void {
    this.selectedCommentAgent = agent;
    this.commentAgentDropdownOpen = false;
  }

  // Reply Agent Dropdown methods
  toggleReplyAgentDropdown(): void {
    if (this.submittingReply || this.loadingPartnerAgents) {
      return;
    }
    this.replyAgentDropdownOpen = !this.replyAgentDropdownOpen;
  }

  closeReplyAgentDropdown(): void {
    this.replyAgentDropdownOpen = false;
  }

  selectReplyAgent(agent: any): void {
    this.selectedReplyAgent = agent;
    this.replyAgentDropdownOpen = false;
  }

  // Invoice search methods
  searchInvoices(searchTerm: string): void {
    this.loadingInvoices = true;
    this.showInvoiceResults = true;

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      Filter: {
        Product: searchTerm,
        Partner: this.Partner, // ALWAYS include Partner from URL
        Pager: {
          PageIndex: 1,
          PageSize: 20,
          TotalCount: 0
        },
        Company: this.Company,
      },
    };

    this.B2BService.GetB2BInvoices(request).subscribe(
      (response: any) => {
        if (response.Status === 1) {
          this.searchedInvoices = response.Invoices || [];
        } else {
          this.openSnackBar(response.Message, 'OK', 'alert-info-snackbar');
          this.searchedInvoices = [];
        }
        this.loadingInvoices = false;
      },
      (error) => {
        console.error('Error searching invoices:', error);
        this.loadingInvoices = false;
        this.searchedInvoices = [];
      }
    );
  }

  selectInvoice(invoice: any): void {
    this.selectedInvoice = invoice;
    this.invoiceSearchTerm = invoice.Reference || invoice.Number || '';
    this.showInvoiceResults = false;
    this.searchedInvoices = [];

    // Load products for the selected invoice
    this.loadInvoiceProducts(invoice.ID);

    // Load partner agents for the Partner
    this.loadNewClaimPartnerAgents();
  }

  // Load partner agents for new claim
  loadNewClaimPartnerAgents(): void {
    if (!this.Partner?.ID) {
      this.newClaimPartnerAgents = [];
      return;
    }

    this.newClaimLoadingPartnerAgents = true;

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      PartnerID: this.Partner.ID
    };

    this.sharedService.GetPartnerAgents(request).subscribe(
      (response: any) => {
        this.newClaimLoadingPartnerAgents = false;
        if (response.Status === 1) {
          this.newClaimPartnerAgents = response.PartnerAgents || [];
          // Mark all as unselected initially
          this.newClaimPartnerAgents.forEach(agent => agent.IsSelected = false);
        } else {
          this.newClaimPartnerAgents = [];
          this.openSnackBar(response.Message || 'Failed to load partner agents', 'OK', 'alert-info-snackbar');
        }
      },
      () => {
        this.newClaimLoadingPartnerAgents = false;
        this.newClaimPartnerAgents = [];
        this.openSnackBar('Failed to load partner agents', 'OK', 'alert-info-snackbar');
      }
    );
  }

  // Toggle partner agent selection for new claim (local only, not saved to DB yet)
  toggleNewClaimPartnerAgent(agent: any): void {
    agent.IsSelected = !agent.IsSelected;
  }

  // Toggle new claim agents section expand/collapse
  toggleNewClaimAgentsSection(): void {
    this.newClaimAgentsSectionExpanded = !this.newClaimAgentsSectionExpanded;
  }

  // Get count of selected partner agents for new claim
  getNewClaimSelectedAgentsCount(): number {
    return this.newClaimPartnerAgents.filter(a => a.IsSelected).length;
  }

  loadInvoiceProducts(invoiceID: string): void {
    this.loadingProducts = true;
    this.claimProducts = [];
    this.productClaimTypes = [];

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      InvoiceID: invoiceID
    };

    this.B2BService.GetInvoiceProductsForClaim(request).subscribe(
      (response: any) => {
        this.loadingProducts = false;
        if (response.Status === 1) {
          this.claimProducts = response.ClaimProducts || [];
          this.productClaimTypes = response.ProductClaimTypes || [];

          // Initialize Proofs array for each product if not present
          this.claimProducts.forEach(product => {
            if (!product.Proofs) {
              product.Proofs = [];
            }
          });
        } else {
          this.openSnackBar(response.Message || 'Failed to load products', 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.loadingProducts = false;
        console.error('Error loading invoice products:', error);
        this.openSnackBar('Failed to load invoice products', 'OK', 'alert-info-snackbar');
      }
    );
  }

  clearInvoiceSearch(): void {
    this.invoiceSearchTerm = '';
    this.selectedInvoice = null;
    this.searchedInvoices = [];
    this.showInvoiceResults = false;
    this.claimProducts = [];
    this.productClaimTypes = [];
  }

  onInvoiceSearchFocus(): void {
    this.invoiceSearchHighlighted = true;
    if (this.invoiceSearchTerm?.length >= 2) {
      this.showInvoiceResults = true;
    }
  }

  onInvoiceSearchBlur(): void {
    this.invoiceSearchHighlighted = false;
  }

  // Focus invoice search input (called when Step 1 is clicked)
  focusInvoiceSearch(): void {
    if (this.invoiceSearchInput) {
      this.invoiceSearchInput.nativeElement.focus();
    }
  }

  // Product-level file upload methods
  onProductFileSelected(event: any, product: ClaimProduct): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const validVideoTypes = ['video/mp4'];
      const validTypes = [...validImageTypes, ...validVideoTypes];

      if (!validTypes.includes(file.type)) {
        this.openSnackBar('Invalid file type. Only JPEG, PNG and MP4 files are allowed.', 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      // Validate file size - 10MB for images, 50MB for videos
      const isVideo = validVideoTypes.includes(file.type);
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      const maxSizeLabel = isVideo ? '50MB' : '10MB';

      if (file.size > maxSize) {
        this.openSnackBar(`File size must be less than ${maxSizeLabel}.`, 'OK', 'alert-info-snackbar');
        event.target.value = '';
        return;
      }

      // Convert to Base64 and add to product's Proofs array
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const proof: ProductClaimProof = {
          ID: 0,
          FKClaimProductID: product.DBID || 0,
          UploadedDocument: null,
          Base64UploadedDocument: base64, // ✅ Backend will convert this to byte[]
          FileName: file.name
        };
        product.Proofs.push(proof);
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    event.target.value = '';
  }

  removeProductProof(product: ClaimProduct, proofIndex: number): void {
    product.Proofs.splice(proofIndex, 1);
  }

  // Only allow digits 0-9 in quantity input
  onQuantityKeyPress(event: KeyboardEvent): boolean {
    // Allow only digits 0-9
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // Handle paste - only allow numeric values
  onQuantityPaste(event: ClipboardEvent): void {
    const pastedData = event.clipboardData?.getData('text') || '';
    // If pasted data contains non-numeric characters, prevent paste
    if (!/^\d+$/.test(pastedData)) {
      event.preventDefault();
    }
  }

  // Quantity validation - ensure ClaimedQuantity doesn't exceed DocQuantity
  validateClaimedQuantity(product: ClaimProduct): void {
    // Convert to number in case it's a string
    let qty = parseInt(product.ClaimedQuantity as any, 10);
    if (isNaN(qty) || qty < 0) {
      qty = 0;
    }
    if (qty > product.DocQuantity) {
      qty = product.DocQuantity;
    }
    product.ClaimedQuantity = qty;
  }

  // Increment quantity
  incrementQuantity(product: ClaimProduct): void {
    let qty = parseInt(product.ClaimedQuantity as any, 10) || 0;
    if (qty < product.DocQuantity) {
      product.ClaimedQuantity = qty + 1;
    }
  }

  // Decrement quantity
  decrementQuantity(product: ClaimProduct): void {
    let qty = parseInt(product.ClaimedQuantity as any, 10) || 0;
    if (qty > 0) {
      product.ClaimedQuantity = qty - 1;
    }
  }

  // Check if any product has a claim (ClaimedQuantity > 0 and ClaimType selected)
  hasValidProductClaims(): boolean {
    return this.claimProducts.some(p => p.ClaimedQuantity > 0 && p.ClaimType != null);
  }

  // Toggle claim type dropdown for a specific product
  toggleClaimTypeDropdown(index: number): void {
    if (this.openClaimTypeDropdownIndex === index) {
      this.openClaimTypeDropdownIndex = -1;
    } else {
      this.openClaimTypeDropdownIndex = index;
    }
  }

  // Select claim type for a product
  selectClaimType(product: ClaimProduct, type: any): void {
    product.ClaimType = type;
    this.openClaimTypeDropdownIndex = -1;
  }

  // Close claim type dropdown
  closeClaimTypeDropdown(): void {
    this.openClaimTypeDropdownIndex = -1;
  }

  // Clear claim type selection
  clearClaimType(product: ClaimProduct, event: Event): void {
    event.stopPropagation(); // Prevent dropdown from opening
    product.ClaimType = null;
    product.UserClaimDescription = null;
    product.Proofs = [];
  }

  // Toggle products section expand/collapse
  toggleProductsSection(): void {
    this.productsExpanded = !this.productsExpanded;
  }

  // Form validation
  isFormValid(): boolean {
    return this.selectedInvoice != null;
  }

  // Validate products with claimed quantities
  validateClaimProducts(): ProductValidationError[] {
    const errors: ProductValidationError[] = [];

    this.claimProducts.forEach(product => {
      if (product.ClaimedQuantity > 0) {
        const error: ProductValidationError = {
          product: product,
          missingClaimType: !product.ClaimType,
          missingDescription: !product.UserClaimDescription || product.UserClaimDescription.trim().length === 0,
          missingProof: !product.Proofs || product.Proofs.length === 0
        };

        // Only add to errors if at least one field is missing
        if (error.missingClaimType || error.missingDescription || error.missingProof) {
          errors.push(error);
        }
      }
    });

    return errors;
  }

  // Dismiss product validation errors
  dismissProductValidationErrors(): void {
    this.showProductValidationErrors = false;
    this.productValidationErrors = [];
  }

  // Submit claim
  submitClaim(): void {
    if (!this.isFormValid()) {
      this.openSnackBar('Please fill in all required fields.', 'OK', 'alert-info-snackbar');
      return;
    }

    // Validate products
    this.productValidationErrors = this.validateClaimProducts();
    if (this.productValidationErrors.length > 0) {
      this.showProductValidationErrors = true;
      // Scroll to the error message
      setTimeout(() => {
        const errorElement = document.querySelector('.ClaimProductValidationError');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    this.submitting = true;
    this.submitSuccess = false;
    this.submitError = '';
    this.showProductValidationErrors = false;

    // Filter products with ClaimedQuantity > 0
    const productsToSubmit = this.claimProducts.filter(p => p.ClaimedQuantity > 0);

    const request = {
      User: this.User,
      Language: this.getLanguage(),
      Claim: {
        Title: this.claimTitle.trim(),
        DebatorPO: this.selectedInvoice?.Reference || this.selectedInvoice?.Number || '',
        Partner: this.Partner,
        ClaimedProducts: productsToSubmit // ✅ Send products with proofs (Base64 files)
      },
      // Include selected partner agent IDs for the new claim
      PartnerAgentIDs: this.newClaimPartnerAgents
        .filter(agent => agent.IsSelected)
        .map(agent => agent.ID)
    };

    this.B2BService.AddNewClaim(request).subscribe(
      (response: any) => {
        this.submitting = false;
        if (response.Status === 1) {
          this.submitSuccess = true;
          this.openSnackBar('Claim submitted successfully!', 'OK', 'light-info-snackbar');
          this.resetForm();
        } else {
          this.submitError = response.Message || 'Failed to submit claim.';
          this.openSnackBar(this.submitError, 'OK', 'alert-info-snackbar');
        }
      },
      (error) => {
        this.submitting = false;
        this.submitError = 'Failed to connect to server.';
        this.openSnackBar(this.submitError, 'OK', 'alert-info-snackbar');
        console.error('Error submitting claim:', error);
      }
    );
  }

  // Reset form
  resetForm(): void {
    this.invoiceSearchTerm = '';
    this.selectedInvoice = null;
    this.searchedInvoices = [];
    this.showInvoiceResults = false;
    this.claimProducts = [];
    this.productClaimTypes = [];
    this.claimTitle = '';
    this.submitSuccess = false;
    this.submitError = '';
    // Clear new claim partner agents
    this.newClaimPartnerAgents = [];
    this.newClaimAgentsSectionExpanded = true;
  }
}
